import type { NavAttentionCounts } from '@/core/hooks/use-nav-attention-counts';
import { shellPaths } from '@/core/router/app-paths';

import type { NavCollapsible, NavGroup, NavItem, NavLink } from './types';

type NavChild = NavCollapsible['items'][number];

/** Numeric pill text for sidebar / More hub; hidden when count is 0. */
export function formatNavAttentionBadge(count: number): string | undefined {
  if (count <= 0) return undefined;
  return count > 9 ? '9+' : String(count);
}

/**
 * Solid primary count pill for sidebar / More hub.
 * Fixed capsule so `1`…`9` and `9+` share one footprint (no grow-with-content).
 * `border-transparent` overrides Badge default `--primary-border` (harsh black outline).
 */
export const NAV_ATTENTION_BADGE_CLASS =
  'h-5 w-6 shrink-0 justify-center rounded-full border-transparent px-0 py-0 text-[11px] font-semibold tabular-nums leading-none';

/** Resolve attention count for a More-hub / shell titleKey. */
export function navAttentionCountForTitleKey(
  titleKey: string,
  counts: NavAttentionCounts,
): number {
  switch (titleKey) {
    case 'leads':
      return counts.leads;
    case 'feedback':
    case 'feedbackActivity':
    case 'feedbackInbox':
      return counts.feedback;
    case 'reputation':
    case 'reviews':
      return counts.reviews;
    case 'products':
      return counts.claims;
    default:
      return 0;
  }
}

function isLeadsPath(url: string): boolean {
  const pathOnly = url.split('?')[0] ?? url;
  return (
    pathOnly === shellPaths.leads || pathOnly.startsWith(`${shellPaths.leads}/`)
  );
}

function isFeedbackPath(url: string): boolean {
  return (
    url === shellPaths.feedbackRoot ||
    url.startsWith(`${shellPaths.feedbackRoot}/`)
  );
}

function isReputationPath(url: string): boolean {
  return (
    url === shellPaths.reputationRoot ||
    url.startsWith(`${shellPaths.reputationRoot}/`)
  );
}

function badgeForUrl(
  url: string,
  counts: NavAttentionCounts,
): string | undefined {
  const pathOnly = url.split('?')[0] ?? url;
  if (pathOnly === shellPaths.leads) {
    return formatNavAttentionBadge(counts.leads);
  }
  if (url === shellPaths.reputationInbox) {
    return formatNavAttentionBadge(counts.reviews);
  }
  if (pathOnly === shellPaths.moreProducts) {
    return formatNavAttentionBadge(counts.claims);
  }
  if (url === shellPaths.feedbackActivity || url === shellPaths.feedbackInbox) {
    return formatNavAttentionBadge(counts.feedback);
  }
  return undefined;
}

function applyChildBadge(
  child: NavChild,
  counts: NavAttentionCounts,
): NavChild {
  // Leads attention stays on the collapsible parent (Alle Leads / Events).
  if (isLeadsPath(child.url)) return child;
  const badge = badgeForUrl(child.url, counts);
  if (!badge) return child;
  return { ...child, badge };
}

function applyLinkBadge(item: NavLink, counts: NavAttentionCounts): NavLink {
  const badge = badgeForUrl(item.url, counts);
  if (!badge) return item;
  return { ...item, badge };
}

function applyCollapsibleBadge(
  item: NavCollapsible,
  counts: NavAttentionCounts,
): NavCollapsible {
  const children = item.items.map((child) => applyChildBadge(child, counts));

  const hasFeedback = item.items.some((child) => isFeedbackPath(child.url));
  const hasReputation = item.items.some((child) => isReputationPath(child.url));
  const hasLeads = item.items.some((child) => isLeadsPath(child.url));

  let groupBadge: string | undefined;
  if (hasLeads) {
    groupBadge = formatNavAttentionBadge(counts.leads);
  } else if (hasFeedback) {
    groupBadge = formatNavAttentionBadge(counts.feedback);
  } else if (hasReputation) {
    groupBadge = formatNavAttentionBadge(counts.reviews);
  }

  const childrenChanged = children.some(
    (child, index) => child.badge !== item.items[index]?.badge,
  );

  if (!groupBadge && !childrenChanged) {
    return item;
  }

  return {
    ...item,
    ...(groupBadge ? { badge: groupBadge } : {}),
    items: children,
  };
}

function applyItemBadge(item: NavItem, counts: NavAttentionCounts): NavItem {
  if (item.items) {
    return applyCollapsibleBadge(item, counts);
  }
  return applyLinkBadge(item, counts);
}

/** Attach Status Board attention counts onto sidebar nav items by path. */
export function applySidebarNavAttentionBadges(
  groups: NavGroup[],
  counts: NavAttentionCounts,
): NavGroup[] {
  if (
    counts.leads <= 0 &&
    counts.feedback <= 0 &&
    counts.reviews <= 0 &&
    counts.claims <= 0
  ) {
    return groups;
  }

  return groups.map((group) => ({
    ...group,
    items: group.items.map((item) => applyItemBadge(item, counts)),
  }));
}

/** True when Cap More tab should show an ops attention dot (excl. notifications). */
export function hasMoreTabOpsAttention(counts: NavAttentionCounts): boolean {
  return counts.feedback > 0 || counts.reviews > 0 || counts.claims > 0;
}
